import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

/**
 * Props for the ImageViewer component
 * @interface ImageViewerProps
 * @property {string} imageUrl - The URL of the image to be displayed
 * @property {string} fileName - The name of the file to be displayed in the header
 * @property {() => void} onClose - Callback function to handle closing the viewer
 */
interface ImageViewerProps {
  imageUrl: string;
  fileName: string;
  onClose: () => void;
}

/**
 * A React component for displaying images in a modal dialog with a responsive layout.
 * Supports various image formats and provides a clean, user-friendly interface.
 * 
 * @component
 * @param {ImageViewerProps} props - The component props
 * @returns {JSX.Element} A dialog containing the image viewer
 * 
 * @example
 * <ImageViewer
 *   imageUrl="https://example.com/image.jpg"
 *   fileName="Sample Image.jpg"
 *   onClose={() => console.log('Viewer closed')}
 * />
 */
const ImageViewer: React.FC<ImageViewerProps> = ({
  imageUrl,
  fileName,
  onClose,
}) => {
  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch image");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download image");
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          style: {
            height: "90vh",
            maxHeight: "90vh",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            aria-label="back"
            onClick={onClose}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            sx={{ maxWidth: "calc(100% - 100px)" }}
          >
            {fileName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.5)",
              textTransform: "none",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Download
          </Button>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={fileName}
          sx={{
            maxWidth: "100%",
            maxHeight: "calc(90vh - 120px)",
            objectFit: "contain",
            borderRadius: 1,
            boxShadow: 1,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImageViewer;
